import { ReactNode } from "react";
import { Navigate } from "react-router";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthStore } from "@/store/useAuthStore";
import { PermissionName } from "@/constants/permissions";
import { getDefaultRoute } from "@/utils/getDefaultRoute";

interface PermissionGuardProps {
  children: ReactNode;
  permissions: PermissionName[];
  requireAll?: boolean; // If true, user must have ALL permissions. If false, user needs ANY permission
  fallback?: ReactNode;
  redirectTo?: string;
}

export function PermissionGuard({
  children,
  permissions,
  requireAll = false,
  fallback,
  redirectTo,
}: PermissionGuardProps) {
  const { hasAllPermissions, hasAnyPermission } = usePermissions();
  const { user } = useAuthStore();

  const hasAccess = requireAll
    ? hasAllPermissions(permissions)
    : hasAnyPermission(permissions);

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }
    // Use smart redirect to user's first accessible route if no custom redirect specified
    const defaultRedirect = redirectTo || getDefaultRoute(user?.permissionNames || []);
    return <Navigate to={defaultRedirect} replace />;
  }

  return <>{children}</>;
}
